import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import AuthRoleSwitch from "../../components/AuthRoleSwitch/AuthRoleSwitch";
import { FormModal } from "../../components/Modals";
import { useAuth } from "../../context/AuthContext";
import { getAuthTokens } from "../../services/authService/authService";
import type { AuthRole } from "../../types/auth";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import { useNotify } from "../../context/NotificationContext";
import { Container, Logo } from "./LoginPage.styles";
import { LoginFormProps } from "./LoginPage.types";

const LoginPage: React.FC<LoginFormProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") === "student" ? "student" : "teacher";
  const next = searchParams.get("next") || "";
  const { login } = useAuth();
  const toast = useNotify();
  const [role, setRole] = useState<AuthRole>(initialRole);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const memoizedExternalErrors = useMemo(() => externalErrors, [externalErrors]);

  const fields = useMemo(
    () => [
      {
        name: "email",
        type: "email",
        placeholder: t("auth.email"),
        required: true,
        width: "369px",
        validate: (value: string) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : t("auth.emailError"),
      },
      {
        name: "password",
        type: "password",
        width: "369px",
        placeholder: t("auth.password"),
        required: true,
        validate: (value: string) =>
          value.trim().length > 0 ? null : t("auth.passwordError"),
      },
    ],
    [t],
  );

  const nextSuffix = next ? `&next=${encodeURIComponent(next)}` : "";
  const footerLinks = [
    { text: t("auth.createAccount"), href: `/signup?role=${role}${nextSuffix}`, variant: "primary" as const },
    { text: t("auth.passwordSos"), href: `/reset-password?role=${role}`, variant: "secondary" as const },
  ];

  const handleSubmit = async (formData: Record<string, string>) => {
    setIsLoading(true);
    setExternalErrors({});
    try {
      await getAuthTokens(formData.email, formData.password, role);
      login(role);
      const roleHome = role === "student" ? "/student/courses" : "/courses";
      const safeNext = role === "student" && next.startsWith("/") && !next.startsWith("//") ? next : roleHome;
      navigate(safeNext, { replace: true });
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          formattedErrors[key] = String(value);
        });
        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка авторизации:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка авторизации", life: 3000 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Logo>LISRENT</Logo>
      <FormModal
        title={t("auth.in")}
        fields={fields}
        onSubmit={handleSubmit}
        hasBackdrop={false}
        footerLinks={footerLinks}
        submitButtonText={t("auth.login")}
        externalErrors={memoizedExternalErrors}
        isLoading={isLoading}
        headerContent={(
          <AuthRoleSwitch
            value={role}
            onChange={setRole}
            disabled={isLoading}
            label="Войти как"
          />
        )}
      />
    </Container>
  );
};

export default LoginPage;